import { useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useMemo } from "react";

import { EnhancedOption, HierarchicalOption } from "@/Components/v2/atoms/Autocomplete/types";
import { Stack } from "@/Components/v2/atoms/Stack";
import LabelledHierarchyAutoComplete from "@/Components/v2/molecule/LabelledAutoComplete/LabelledHierarchyAutoComplete";
import { NodeHierarchyAlert } from "@/Components/v2/organism/NodeHierarchyAlert/NodeHiearchyAlert";
import { EN_DASH } from "@/pages/scenario-builder/constants";
import { NodeHierarchy, Nodes } from "@/pages/scenario-builder/utils/nodes";
import { formatDateLocale } from "@/shared/utils/formatDate";
import { getLayer, Layer } from "@/Utils/hierarchy";

interface HeaderProps {
  nodes: Nodes;
  targetNode: string;
  setTargetNode: (targetNode: string) => void;
  minDate?: string | null;
  maxDate?: string | null;
}

export const Header: React.FC<HeaderProps> = ({
  nodes,
  targetNode,
  setTargetNode,
  minDate,
  maxDate,
}) => {
  const theme = useTheme();

  const businessUnitHierarchyOptions: HierarchicalOption[] = useMemo(() => {
    const rootNodeId = 0;

    const mapNodeToOption = (node: NodeHierarchy): HierarchicalOption => {
      const nodeLayer = node.combinedNode && getLayer(node.combinedNode.warchestNode.layer);
      const isDisabledLevel = nodeLayer === Layer.Product || nodeLayer === Layer.Category;

      return {
        id: node.id,
        label: node.label,
        value: node.uuid,
        children: node.children.map(mapNodeToOption),
        alwaysOpen: node.id === rootNodeId,
        selectable: !isDisabledLevel,
      };
    };

    const hierarchicalOptions = nodes.getHierarchy();
    const formattedOptions = hierarchicalOptions.map(mapNodeToOption);
    return formattedOptions;
  }, [nodes]);

  const handleBusinessUnitSelectionChange = (selected: EnhancedOption) => {
    setTargetNode(selected.value);
  };

  return (
    <Stack
      direction="column"
      spacing={2}
      marginX={4}
      marginY={2}
      padding={4}
      paddingY={3}
      border={`1px solid ${theme.palette.section.border}`}
      borderRadius={4}
    >
      <Stack direction="column" position="relative" spacing={1}>
        <LabelledHierarchyAutoComplete
          label="Business Unit"
          id="business-unit"
          disableClearable
          options={businessUnitHierarchyOptions}
          onSelectChange={handleBusinessUnitSelectionChange}
          value={targetNode}
          width={280}
          infoTitle="Available Date Range"
          infoText={`${formatDateLocale(dayjs(minDate))} ${EN_DASH} ${formatDateLocale(dayjs(maxDate))}`}
        />
      </Stack>
      <NodeHierarchyAlert nodeUUID={targetNode} />
    </Stack>
  );
};

export default Header;
